/**
 * Reading a country out of the free text an enrichment provider calls a location.
 *
 * What actually arrives is prose: "Greater Jakarta Area, Indonesia", "Jakarta",
 * "Bali, Indonesia", "Remote", "London, United Kingdom", "SF Bay Area". None of
 * it is structured and about a third of it names no country at all.
 *
 * So this resolves in two passes and gives up quickly. A country name or code at
 * the end of the string is the easy case and is most of the data. Failing that,
 * a small table of cities big enough to be unambiguous is tried, because
 * "Jakarta" alone is common and is not really ambiguous to a human. Everything
 * else returns null, and null is a fine answer: the language inference falls
 * through to reading the name, and the console says the country is not known.
 *
 * Guessing is worse than not knowing here. A location resolved wrong writes a
 * message in the wrong language and nobody finds out, whereas a location left
 * null writes English, which is never actively wrong.
 */

/** Country names as they arrive, mapped to ISO 3166-1 alpha-2. Lowercased keys. */
const COUNTRY_NAMES: Record<string, string> = {
  indonesia: 'ID',
  philippines: 'PH',
  'the philippines': 'PH',
  singapore: 'SG',
  malaysia: 'MY',
  brunei: 'BN',
  thailand: 'TH',
  vietnam: 'VN',
  'viet nam': 'VN',
  cambodia: 'KH',
  japan: 'JP',
  'south korea': 'KR',
  korea: 'KR',
  china: 'CN',
  taiwan: 'TW',
  'hong kong': 'HK',
  india: 'IN',
  pakistan: 'PK',
  bangladesh: 'BD',
  'sri lanka': 'LK',
  'united arab emirates': 'AE',
  uae: 'AE',
  'saudi arabia': 'SA',
  qatar: 'QA',
  kuwait: 'KW',
  bahrain: 'BH',
  oman: 'OM',
  egypt: 'EG',
  jordan: 'JO',
  lebanon: 'LB',
  morocco: 'MA',
  tunisia: 'TN',
  algeria: 'DZ',
  turkey: 'TR',
  türkiye: 'TR',
  turkiye: 'TR',
  netherlands: 'NL',
  'the netherlands': 'NL',
  holland: 'NL',
  sweden: 'SE',
  norway: 'NO',
  denmark: 'DK',
  finland: 'FI',
  germany: 'DE',
  deutschland: 'DE',
  austria: 'AT',
  switzerland: 'CH',
  'united kingdom': 'GB',
  uk: 'GB',
  england: 'GB',
  scotland: 'GB',
  wales: 'GB',
  'northern ireland': 'GB',
  ireland: 'IE',
  france: 'FR',
  belgium: 'BE',
  spain: 'ES',
  españa: 'ES',
  portugal: 'PT',
  italy: 'IT',
  italia: 'IT',
  poland: 'PL',
  czechia: 'CZ',
  'czech republic': 'CZ',
  slovakia: 'SK',
  hungary: 'HU',
  romania: 'RO',
  bulgaria: 'BG',
  greece: 'GR',
  croatia: 'HR',
  serbia: 'RS',
  estonia: 'EE',
  latvia: 'LV',
  lithuania: 'LT',
  slovenia: 'SI',
  luxembourg: 'LU',
  iceland: 'IS',
  malta: 'MT',
  cyprus: 'CY',
  russia: 'RU',
  ukraine: 'UA',
  'united states': 'US',
  'united states of america': 'US',
  usa: 'US',
  us: 'US',
  america: 'US',
  canada: 'CA',
  mexico: 'MX',
  méxico: 'MX',
  brazil: 'BR',
  brasil: 'BR',
  argentina: 'AR',
  colombia: 'CO',
  chile: 'CL',
  peru: 'PE',
  nigeria: 'NG',
  kenya: 'KE',
  ghana: 'GH',
  'south africa': 'ZA',
  australia: 'AU',
  'new zealand': 'NZ',
}

/**
 * Cities that name their country on their own.
 *
 * Only cities where a person would not ask "which one". Springfield is not here
 * and never will be; nor is any city whose name is shared across two of the
 * markets in the table, because the whole value of this pass is that it does not
 * need a tiebreak.
 */
const CITIES: Record<string, string> = {
  jakarta: 'ID',
  surabaya: 'ID',
  bandung: 'ID',
  bali: 'ID',
  denpasar: 'ID',
  yogyakarta: 'ID',
  medan: 'ID',
  makassar: 'ID',
  manila: 'PH',
  'quezon city': 'PH',
  cebu: 'PH',
  davao: 'PH',
  'kuala lumpur': 'MY',
  bangkok: 'TH',
  hanoi: 'VN',
  'ho chi minh city': 'VN',
  saigon: 'VN',
  tokyo: 'JP',
  osaka: 'JP',
  seoul: 'KR',
  shanghai: 'CN',
  beijing: 'CN',
  shenzhen: 'CN',
  mumbai: 'IN',
  delhi: 'IN',
  'new delhi': 'IN',
  bengaluru: 'IN',
  bangalore: 'IN',
  hyderabad: 'IN',
  karachi: 'PK',
  lahore: 'PK',
  islamabad: 'PK',
  dhaka: 'BD',
  dubai: 'AE',
  'abu dhabi': 'AE',
  riyadh: 'SA',
  jeddah: 'SA',
  doha: 'QA',
  cairo: 'EG',
  casablanca: 'MA',
  rabat: 'MA',
  istanbul: 'TR',
  ankara: 'TR',
  amsterdam: 'NL',
  rotterdam: 'NL',
  'the hague': 'NL',
  utrecht: 'NL',
  stockholm: 'SE',
  oslo: 'NO',
  copenhagen: 'DK',
  helsinki: 'FI',
  berlin: 'DE',
  munich: 'DE',
  münchen: 'DE',
  hamburg: 'DE',
  frankfurt: 'DE',
  vienna: 'AT',
  zurich: 'CH',
  zürich: 'CH',
  geneva: 'CH',
  london: 'GB',
  manchester: 'GB',
  birmingham: 'GB',
  edinburgh: 'GB',
  glasgow: 'GB',
  leeds: 'GB',
  bristol: 'GB',
  dublin: 'IE',
  paris: 'FR',
  lyon: 'FR',
  marseille: 'FR',
  toulouse: 'FR',
  brussels: 'BE',
  antwerp: 'BE',
  madrid: 'ES',
  barcelona: 'ES',
  valencia: 'ES',
  lisbon: 'PT',
  porto: 'PT',
  milan: 'IT',
  milano: 'IT',
  rome: 'IT',
  roma: 'IT',
  turin: 'IT',
  warsaw: 'PL',
  krakow: 'PL',
  kraków: 'PL',
  moscow: 'RU',
  'saint petersburg': 'RU',
  kyiv: 'UA',
  kiev: 'UA',
  'new york': 'US',
  'san francisco': 'US',
  'los angeles': 'US',
  chicago: 'US',
  boston: 'US',
  seattle: 'US',
  austin: 'US',
  denver: 'US',
  atlanta: 'US',
  miami: 'US',
  toronto: 'CA',
  vancouver: 'CA',
  montreal: 'CA',
  montréal: 'CA',
  calgary: 'CA',
  ottawa: 'CA',
  'mexico city': 'MX',
  guadalajara: 'MX',
  'sao paulo': 'BR',
  'são paulo': 'BR',
  'rio de janeiro': 'BR',
  'buenos aires': 'AR',
  bogota: 'CO',
  bogotá: 'CO',
  santiago: 'CL',
  lima: 'PE',
  lagos: 'NG',
  abuja: 'NG',
  nairobi: 'KE',
  accra: 'GH',
  johannesburg: 'ZA',
  'cape town': 'ZA',
  sydney: 'AU',
  melbourne: 'AU',
  brisbane: 'AU',
  perth: 'AU',
  auckland: 'NZ',
  wellington: 'NZ',
}

/** ISO codes, so an already-clean value passes straight through. */
const ISO_CODES = new Set(Object.values(COUNTRY_NAMES))

/**
 * The country a free-text location names, or null.
 *
 * Null is a normal result, not a failure. About a third of real locations name
 * no country and inventing one for them would be the only way this function
 * could do harm.
 */
export function countryFromLocation(location: string | null | undefined): string | null {
  const raw = (location ?? '').trim()
  if (raw === '') return null

  // Already a code. Two letters on their own is only a country when it is one
  // we know, so "CA" resolves and "XQ" does not.
  const bare = raw.toUpperCase()
  if (/^[A-Z]{2}$/.test(bare) && ISO_CODES.has(bare)) return bare

  const parts = raw
    .split(/[,/|]/)
    .map((part) => normalise(part))
    .filter((part) => part !== '')
  if (parts.length === 0) return null

  // Last part first: "Jakarta, Indonesia" puts the country at the end, and so
  // does almost every location string a provider emits.
  for (let index = parts.length - 1; index >= 0; index -= 1) {
    const part = parts[index] ?? ''
    const named = COUNTRY_NAMES[part]
    if (named !== undefined) return named
    const upper = part.toUpperCase()
    if (/^[A-Z]{2}$/.test(upper) && ISO_CODES.has(upper)) return upper
  }

  // Then the cities, in the same order. "Greater Jakarta Area" reduces to
  // "jakarta" through the noise words below.
  for (let index = 0; index < parts.length; index += 1) {
    const city = CITIES[parts[index] ?? '']
    if (city !== undefined) return city
  }

  return null
}

/**
 * Lowercase, fold accents, and drop the words providers pad a location with.
 *
 * "Greater Jakarta Area" and "Jakarta Metropolitan Area" both have to reduce to
 * "jakarta" or the city table catches almost nothing real.
 */
function normalise(part: string): string {
  return part
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, (char) => (/[̀-ͯ]/.test(char) ? '' : char))
    .toLowerCase()
    .replace(/\b(greater|metropolitan|metro|area|region|province|city of|and surrounds)\b/g, ' ')
    .replace(/[^a-z\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}
